import {
  Grid,
  GridItem,
  Heading,
  Text,
  theme,
  Box,
  VStack,
} from '@everlywell/ui-kit';
import Carousel from 'components/Carousel';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import RichTextWrapper from 'components/RichTextWrapper';
import StepCard, { Card } from 'components/StepCard';
import React from 'react';
import { ContentfulRichText } from 'utils/contentful/renderRichText';

// Types ----------

export interface StepCardListProps {
  cards: Card[];
  eyebrow: string;
  title: string;
  titleAppend?: string;
  body?: ContentfulRichText;
}

// StepCardList ------

const StepCardList: React.FC<StepCardListProps | null> = (props) => {
  if (!props) {
    return null;
  }

  const { eyebrow, title, cards, titleAppend, body } = props;

  const isCardsOnNewRow = cards.length > 2;

  return (
    <SectionContainer size={['sm', 'sm', 'xl', 'xl', 'xl']} overflowY="clip">
      <Grid alignEdges spacing={[6, 6, 5]} maxWidth={['100vw', '100%']}>
        <GridItem width={[1, isCardsOnNewRow ? 1 : 1 / 3]}>
          <Text
            size="xl"
            marginBottom={['1', null, '2']}
            color={theme.colors.viridian.base}
            textAlign={{
              base: 'center',
              largeTablet: 'left',
            }}
          >
            {eyebrow}
          </Text>

          <Heading
            size={['lg', null, '2xl']}
            textAlign={{
              base: 'center',
              largeTablet: 'left',
            }}
            marginBottom={[10, null, 20]}
          >
            {title} {titleAppend ? titleAppend : ''}
          </Heading>
        </GridItem>

        <GridItem width={[1, isCardsOnNewRow ? 1 : 2 / 3]}>
          {/* mobile and tablet */}
          <VStack
            spacing={8}
            display={{
              base: 'flex',
              largeTablet: 'none',
            }}
            maxWidth="450px"
            marginX="auto"
          >
            {cards.map((card, index) => (
              <StepCard
                card={card}
                index={index}
                key={index}
                isLastCard={index === cards.length - 1}
              />
            ))}
          </VStack>
          {/* desktop */}
          <Box
            display={{
              base: 'none',
              largeTablet: 'initial',
            }}
            sx={{
              '& .swiper': {
                overflow: [null, null, 'visible'],
              },

              '& .swiper-wrapper': {
                alignItems: 'stretch',
              },
              '& .swiper-slide': {
                height: 'auto',
              },
            }}
          >
            <Carousel
              slidesPerView={Math.min(cards.length, 4)}
              pagination={{
                clickable: true,
                enabled: cards.length > 1,
              }}
              spaceBetween={16}
              showPagination
            >
              {cards.map((card, index) => (
                <StepCard
                  key={`step-card-${index}`}
                  card={card}
                  index={index}
                  isLastCard={index === cards.length - 1}
                  imageStyles={{
                    aspectRatio: [
                      '1.5',
                      null,
                      null,
                      cards.length > 3 ? '1.5' : '2',
                    ],
                  }}
                />
              ))}
            </Carousel>
            <Box></Box>
          </Box>
        </GridItem>
      </Grid>

      {body && body?.raw ? (
        <Box marginTop="4">
          <RichTextWrapper contentfulRichText={body} />
        </Box>
      ) : null}
    </SectionContainer>
  );
};

// Export ---------

export default StepCardList;
